// Client API assistant IA — P3.

import { apiCall } from "./client";
import type { BriefPlan, EmailClassification, EmailSummary, FeedbackInput } from "./types";

export async function getBriefToday(at?: "morning" | "evening"): Promise<BriefPlan | null> {
  try {
    const res = await apiCall<BriefPlan>("/api/assistant/brief/today", {
      query: at ? { at } : {},
    });
    return res.data;
  } catch (err) {
    if (err instanceof Error && "status" in err && (err as { status: number }).status === 404) {
      return null;
    }
    throw err;
  }
}

export async function classifyEmail(emailId: string): Promise<EmailClassification> {
  const res = await apiCall<EmailClassification>(`/api/emails/${emailId}/classify`, {
    method: "POST",
    idempotencyKey: crypto.randomUUID(),
  });
  return res.data;
}

export async function summarizeEmail(emailId: string): Promise<EmailSummary> {
  const res = await apiCall<EmailSummary>(`/api/emails/${emailId}/summarize`, {
    method: "POST",
    idempotencyKey: crypto.randomUUID(),
  });
  return res.data;
}

export async function sendFeedback(emailId: string, body: FeedbackInput): Promise<void> {
  await apiCall(`/api/emails/${emailId}/feedback`, {
    method: "POST",
    body,
    idempotencyKey: crypto.randomUUID(),
  });
}

// ============================================================================
// P7 — Chat conversationnel SSE
// ============================================================================

export interface AssistantConversation {
  id: string;
  title: string | null;
  context_json: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface AssistantMessage {
  id: string;
  conversation_id: string;
  role: "user" | "assistant" | "tool";
  content: string;
  tool_calls: Record<string, unknown> | null;
  tool_results: Record<string, unknown> | null;
  tokens_used: number | null;
  model_version: string | null;
  created_at: string;
}

export interface PlanStep {
  step_id: string;
  tool: string;
  args: Record<string, unknown>;
  human_label: string;
  requires_user_confirmation?: boolean;
  dry_run_preview?: boolean;
}

export interface PlanJson {
  plan_id: string;
  summary_for_user: string;
  steps: PlanStep[];
  estimated_destructive_actions: number;
  estimated_outbound_actions: number;
}

export interface AssistantPlan {
  id: string;
  conversation_id: string | null;
  initial_prompt: string;
  plan_json: PlanJson;
  status: "proposed" | "approved" | "rejected" | "executed" | "failed";
  validated_at: string | null;
  executed_at: string | null;
  result_json: {
    steps: { step_id: string; status: string; error?: string }[];
    partial: boolean;
  } | null;
  created_at: string;
}

export async function listConversations(q?: string): Promise<AssistantConversation[]> {
  const res = await apiCall<AssistantConversation[]>("/api/assistant/conversations", {
    query: q ? { q } : {},
  });
  return res.data;
}

export async function listMessages(conversationId: string): Promise<AssistantMessage[]> {
  const res = await apiCall<AssistantMessage[]>(
    `/api/assistant/conversations/${conversationId}/messages`,
  );
  return res.data;
}

export async function deleteConversation(id: string): Promise<void> {
  await apiCall(`/api/assistant/conversations/${id}`, { method: "DELETE" });
}

export async function approvePlan(
  planId: string,
  accountId: string,
): Promise<AssistantPlan["result_json"]> {
  const res = await apiCall<{
    plan_id: string;
    status: string;
    result: AssistantPlan["result_json"];
  }>(`/api/assistant/plans/${planId}/approve`, {
    method: "POST",
    body: { account_id: accountId },
    idempotencyKey: crypto.randomUUID(),
  });
  return res.data.result;
}

export async function rejectPlan(planId: string): Promise<void> {
  await apiCall(`/api/assistant/plans/${planId}/reject`, {
    method: "POST",
    idempotencyKey: crypto.randomUUID(),
  });
}

export type ChatStreamEvent =
  | { type: "token"; data: { delta: string } }
  | { type: "plan"; data: { plan_id: string; plan_json: PlanJson } }
  | { type: "error"; data: { code: string; message: string } }
  | { type: "done"; data: { message_id: string; conversation_id: string; plan_id: string } };

async function openChatRequest(
  userMessage: string,
  conversationId?: string,
): Promise<ReadableStreamDefaultReader<Uint8Array>> {
  const body = JSON.stringify({
    user_message: userMessage,
    ...(conversationId ? { conversation_id: conversationId } : {}),
  });
  const res = await fetch("/api/assistant/chat", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Idempotency-Key": crypto.randomUUID(),
    },
    body,
    credentials: "include",
  });
  if (!res.ok || !res.body) {
    const text = await res.text();
    throw new Error(`chat ${res.status}: ${text}`);
  }
  return res.body.getReader();
}

/**
 * POST /api/assistant/chat (SSE). Stream les events token/plan/error/done.
 * Utilise fetch + ReadableStream (EventSource ne supporte pas POST).
 */
export async function* chatStream(
  userMessage: string,
  conversationId?: string,
): AsyncGenerator<ChatStreamEvent> {
  const reader = await openChatRequest(userMessage, conversationId);
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const events = parseSseBuffer(buf);
    buf = events.rest;
    for (const ev of events.events) yield ev;
  }
}

function parseSseBuffer(buf: string): { events: ChatStreamEvent[]; rest: string } {
  const events: ChatStreamEvent[] = [];
  const blocks = buf.split("\n\n");
  const rest = blocks.pop() ?? "";
  for (const block of blocks) {
    const ev = parseSseBlock(block);
    if (ev) events.push(ev);
  }
  return { events, rest };
}

function parseSseBlock(block: string): ChatStreamEvent | null {
  let type: string | null = null;
  let data: string | null = null;
  for (const line of block.split("\n")) {
    if (line.startsWith("event: ")) type = line.slice(7).trim();
    else if (line.startsWith("data: ")) data = line.slice(6);
  }
  if (!type || data === null) return null;
  try {
    const parsedData: unknown = JSON.parse(data);
    const ev = { type, data: parsedData } as unknown as ChatStreamEvent;
    return ev;
  } catch {
    return null;
  }
}
